import * as React from 'react'
import { PLANT_DCS_NODE_QUERY_PARAM, plantHref } from '@atbash-labs/plantview-links'
import { usePlantSessionPaint, type PlantDcsTreeNode } from '@axilon-apps/domains-ontology-ui-state'
import {
  PlantTreeNav,
  type PlantTreeNavGhost,
  type PlantTreeNavGroup,
  type PlantTreeNavNode
} from './PlantTreeNav'
import { collectUnplacedGhosts } from './session-mark-ui'
import { dcsIconFor } from './dcs-icon'

/** Child kinds that ghost under a DcsController. */
const CONTROLLER_CHILD_LABELS = new Set(['Cee', 'IoModule'])
/** Child kinds that ghost under a Cee. */
const CEE_CHILD_LABELS = new Set(['ControlModule'])
/** Child kinds that ghost under a ControlModule. */
const MODULE_CHILD_LABELS = new Set(['FunctionBlock'])
/** Child kinds that ghost under an IoModule. */
const IO_MODULE_CHILD_LABELS = new Set(['IoChannel'])

/**
 * DCS tab adapter over {@link PlantTreeNav}. Controllers hang CEEs and
 * I/O modules in labeled groups; control modules nest under their CEE
 * and function blocks under their module; channels nest under I/O
 * modules. Controllers start collapsed so large Experion fleets stay
 * scannable — ancestor auto-expand still reveals a deep-linked
 * `?node=<id>` selection.
 */
export interface DcsTreeNavProps {
  nodes: PlantDcsTreeNode[]
  selectedId?: string
  isLoading?: boolean
  className?: string
}

function collectGhosts(
  containerId: string,
  labels: ReadonlySet<string>,
  paintOn: boolean,
  ghostsByContainerId: ReturnType<typeof usePlantSessionPaint>['ghostsByContainerId'],
  markFor: ReturnType<typeof usePlantSessionPaint>['markFor']
): PlantTreeNavGhost[] | undefined {
  if (!paintOn) return undefined
  const ghosts: PlantTreeNavGhost[] = []
  for (const ghost of ghostsByContainerId.get(containerId) ?? []) {
    if (!ghost.labels.some((label) => labels.has(label))) continue
    const view = markFor(ghost.id)
    if (view !== 'deleted' && view !== 'dimmed') continue
    ghosts.push({ id: ghost.id, name: ghost.name ?? ghost.id, view })
  }
  return ghosts.length > 0 ? ghosts : undefined
}

function buildNavNodes(
  nodes: PlantDcsTreeNode[],
  paintOn: boolean,
  markFor: ReturnType<typeof usePlantSessionPaint>['markFor'],
  ghostsByContainerId: ReturnType<typeof usePlantSessionPaint>['ghostsByContainerId']
): PlantTreeNavNode[] {
  const toNavNode = (
    node: PlantDcsTreeNode,
    groups: PlantTreeNavGroup[] = [],
    ghosts?: PlantTreeNavGhost[]
  ): PlantTreeNavNode => ({
    id: node.id,
    href: plantHref(node.id, node.kind, node),
    label: node.name,
    icon: dcsIconFor(node.kind),
    count: node.childCount > 0 ? node.childCount : undefined,
    mark: paintOn ? markFor(node.id) : undefined,
    ghosts,
    groups
  })

  const childrenOf = (parentId: string, kind: PlantDcsTreeNode['kind']): PlantDcsTreeNode[] =>
    nodes.filter((node) => node.parentId === parentId && node.kind === kind)

  const toChannelNode = (channel: PlantDcsTreeNode): PlantTreeNavNode => toNavNode(channel)

  const toIoModuleNode = (ioModule: PlantDcsTreeNode): PlantTreeNavNode => {
    const channels = childrenOf(ioModule.id, 'IoChannel')
    return toNavNode(
      ioModule,
      [{ nodes: channels.map(toChannelNode) }],
      collectGhosts(ioModule.id, IO_MODULE_CHILD_LABELS, paintOn, ghostsByContainerId, markFor)
    )
  }

  const toBlockNode = (block: PlantDcsTreeNode): PlantTreeNavNode => toNavNode(block)

  const toModuleNode = (module: PlantDcsTreeNode): PlantTreeNavNode => {
    const blocks = childrenOf(module.id, 'FunctionBlock')
    return toNavNode(
      module,
      [{ nodes: blocks.map(toBlockNode) }],
      collectGhosts(module.id, MODULE_CHILD_LABELS, paintOn, ghostsByContainerId, markFor)
    )
  }

  const toCeeNode = (cee: PlantDcsTreeNode): PlantTreeNavNode => {
    const modules = childrenOf(cee.id, 'ControlModule')
    return toNavNode(
      cee,
      [{ nodes: modules.map(toModuleNode) }],
      collectGhosts(cee.id, CEE_CHILD_LABELS, paintOn, ghostsByContainerId, markFor)
    )
  }

  const roots: PlantTreeNavNode[] = []
  for (const node of nodes) {
    if (node.kind !== 'DcsController') continue
    const cees = childrenOf(node.id, 'Cee').map(toCeeNode)
    const ioModules = childrenOf(node.id, 'IoModule').map(toIoModuleNode)
    roots.push(
      toNavNode(
        node,
        [
          { label: 'CEEs', nodes: cees },
          { label: 'I/O', nodes: ioModules }
        ],
        collectGhosts(node.id, CONTROLLER_CHILD_LABELS, paintOn, ghostsByContainerId, markFor)
      )
    )
  }
  return roots
}

export function DcsTreeNav({
  nodes,
  selectedId,
  isLoading,
  className
}: DcsTreeNavProps): React.ReactElement {
  const { active, filter, markFor, ghostsByContainerId, markById } = usePlantSessionPaint()
  const paintOn = active && filter.enabled

  const navNodes = React.useMemo(
    () => buildNavNodes(nodes, paintOn, markFor, ghostsByContainerId),
    [ghostsByContainerId, markFor, nodes, paintOn]
  )

  const rootGhosts = React.useMemo((): PlantTreeNavGhost[] | undefined => {
    if (!paintOn) return undefined
    const liveIds = new Set(nodes.map((node) => node.id))
    const unplaced = collectUnplacedGhosts(markById, ghostsByContainerId, markFor, {
      label: 'DcsController',
      liveIds
    })
    if (unplaced.length === 0) return undefined
    return unplaced.map((ghost) => ({
      id: ghost.id,
      name: ghost.name ?? ghost.id,
      view: ghost.view
    }))
  }, [ghostsByContainerId, markById, markFor, nodes, paintOn])

  return (
    <PlantTreeNav
      heading="DCS tree"
      nodes={navNodes}
      selectParam={PLANT_DCS_NODE_QUERY_PARAM}
      selectedId={selectedId}
      isLoading={isLoading}
      loadingText="Loading controllers…"
      emptyTitle="No controllers"
      emptyHint="Ingest an Experion ERDB from the Ingest tab to populate the DCS tree."
      defaultExpanded={false}
      rootGhosts={rootGhosts}
      className={className}
    />
  )
}
